import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { Pagination } from '@/dsm-components/pagination';
import { ProgramTypeTags } from '@/dsm-components/shared/ProgramTypeTags';
import { formatDate } from '@/helpers/dates';
import { type PromoRow, useGetPromosQuery } from '@/services/promos';
import {
  type DataTableColumn,
  PalmettoButton,
  PalmettoDataTable,
  PalmettoIcon,
  PalmettoTag,
} from '@palmetto/dsm-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type PromoUtility } from 'types';

import {
  coversAllStates,
  coversAllUtilities,
  formatPromoValue,
  getPromoStatus,
  PromoStatusLabels,
  PromoStatusTagColors,
  PromoValueTypeLabels,
} from './promoDisplay';
import { toUtilityOptionValue } from './promoFormModel';
import { PromoModal } from './PromoModal';
import { usePromoAvailableStates } from './usePromoUtilityOptions';
import { UtilityTags } from './UtilityTags';

const ALL_UTILITIES_LABELS = ['All utilities'];

// The list response's utilities are looked up by lseId alone, so they include other states' markets;
// "all" is judged against the full fetch only.
const NO_KNOWN_UTILITIES: PromoUtility[] = [];

const addIcon = <PalmettoIcon name="add" size="sm" />;

const pageSize = 25;

const pageParam = (raw: string | null): number => {
  const parsed = Math.floor(Number(raw));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

// DataTable body rows add a 2px gap between cells that the header row lacks, so cells drift right of their header.
const ALIGN_BODY_WITH_HEADER_CLASS = '[&_[role=row]]:gap-0';

type ModalState = { mode: 'create' } | { mode: 'edit'; promo: PromoRow } | null;

interface EditPromoButtonProps {
  promo: PromoRow;
  onEdit: (promo: PromoRow) => void;
}

const EditPromoButton = ({ promo, onEdit }: EditPromoButtonProps) => {
  const handleClick = useCallback(() => onEdit(promo), [onEdit, promo]);

  return (
    <PalmettoButton
      type="button"
      emphasis="tertiary"
      size="xs"
      layout="hug"
      aria-label={`Edit ${promo.code}`}
      onClick={handleClick}
    >
      Edit
    </PalmettoButton>
  );
};

export const PromosTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageParam(searchParams.get('page'));

  const { data, isLoading, isFetching, error, refetch } = useGetPromosQuery({
    limit: pageSize,
    offset: page * pageSize,
  });
  const [modal, setModal] = useState<ModalState>(null);

  const promos = useMemo(() => data?.promos ?? [], [data?.promos]);

  const utilities = useMemo(() => data?.utilities ?? [], [data?.utilities]);

  const utilityNames = useMemo(
    () => new Map(utilities.map((utility) => [utility.lseId, utility.name])),
    [utilities],
  );

  const { states: availableStates, utilityKeys: availableUtilityKeys } = usePromoAvailableStates(NO_KNOWN_UTILITIES);

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  useEffect(() => {
    if (!data || isFetching) return;
    const lastPage = Math.max(0, Math.ceil(data.total / pageSize) - 1);
    if (page <= lastPage) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (lastPage === 0) next.delete('page');
        else next.set('page', String(lastPage));
        return next;
      },
      { replace: true },
    );
  }, [data, isFetching, page, setSearchParams]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(nextPage));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const hasRows = !isLoading && !isFetching && !error && promos.length > 0;

  const handleCreate = useCallback(() => setModal({ mode: 'create' }), []);
  const handleEdit = useCallback((promo: PromoRow) => setModal({ mode: 'edit', promo }), []);
  const handleClose = useCallback(() => setModal(null), []);
  const getRowId = useCallback((row: PromoRow) => row.id, []);

  const columns: DataTableColumn<PromoRow>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', width: '180px', cellType: 'bold', render: (row) => row.code },
      { id: 'name', header: 'Promo', minWidth: 240, render: (row) => row.name },
      {
        id: 'states',
        header: 'State',
        width: '170px',
        render: (row) => {
          const promoStates = row.utilities.map((utility) => utility.state);
          return coversAllStates(promoStates, availableStates)
            ? `All states (${promoStates.length})`
            : promoStates.join(', ');
        },
      },
      {
        id: 'utilities',
        header: 'Utility',
        width: '380px',
        cellType: 'tag',
        render: (row) => {
          const promoKeys = row.utilities.flatMap(({ state, lseIds }) =>
            lseIds.map((lseId) => toUtilityOptionValue(state, lseId)),
          );
          if (coversAllUtilities(promoKeys, availableUtilityKeys))
            return <UtilityTags names={ALL_UTILITIES_LABELS} />;
          return (
            <UtilityTags
              names={row.utilities
                .flatMap((utility) => utility.lseIds)
                .map((lseId) => utilityNames.get(lseId) ?? String(lseId))}
            />
          );
        },
      },
      {
        id: 'programTypes',
        header: 'Program Types',
        width: '300px',
        cellType: 'tag',
        render: (row) => <ProgramTypeTags programTypes={row.programTypes} />,
      },
      {
        id: 'valid',
        header: 'Valid',
        width: '280px',
        render: (row) =>
          `${formatDate(row.validFrom)} - ${row.validThrough ? formatDate(row.validThrough) : 'No end date'}`,
      },
      {
        id: 'valueType',
        header: 'Value Type',
        width: '140px',
        render: (row) => PromoValueTypeLabels[row.valueRule.type],
      },
      { id: 'value', header: 'Value', width: '140px', render: (row) => formatPromoValue(row.valueRule) },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cellType: 'tag',
        render: (row) => {
          const status = getPromoStatus(row);
          return <PalmettoTag label={PromoStatusLabels[status]} color={PromoStatusTagColors[status]} />;
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        width: '100px',
        cellType: 'button',
        render: (row) => <EditPromoButton promo={row} onEdit={handleEdit} />,
      },
    ],
    [utilityNames, availableStates, availableUtilityKeys, handleEdit],
  );

  return (
    <DsmPageContainer title="Promo Management">
      <PageHeader title="Promo Management">
        <PalmettoButton type="button" emphasis="primary" leadingIcon={addIcon} onClick={handleCreate}>
          Create promo
        </PalmettoButton>
      </PageHeader>

      <div className="mx-6 mt-4 overflow-x-auto rounded-16 border border-border-default shadow-[0px_2px_0px_0px_var(--canvas-highlight-bold)]">
        <PalmettoDataTable
          type="uncontained"
          columns={columns}
          data={promos}
          getRowId={getRowId}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          ariaLabel="Promos"
          className={hasRows ? `min-w-[2020px] ${ALIGN_BODY_WITH_HEADER_CLASS}` : undefined}
        />
      </div>

      {!error && totalPages > 1 && (
        <div className="mx-6 mt-4">
          <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}

      <PromoModal
        utilities={utilities}
        open={modal !== null}
        promo={modal?.mode === 'edit' ? modal.promo : null}
        onClose={handleClose}
      />
    </DsmPageContainer>
  );
};
